import { Injectable } from '@angular/core';

export type ThemePref = 'light' | 'dark' | 'system';

export function isThemePref(value: unknown): value is ThemePref {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Light/dark appearance (docs/KONZEPT-Developer-Platform.md). The choice is made in My-SPA and stored on the
 * account, so it is the same in every app ('light' | 'dark' | 'system'); localStorage only caches it so the
 * first paint after a reload already has the right colors. Until an account value arrives, and for an account
 * that never chose one, this app keeps its original dark look (Postman/GitHub/VS Code convention for developer
 * tools, theme/devtools.scss).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'developer.theme';
  private readonly defaultTheme: ThemePref = 'dark';
  private readonly media = window.matchMedia('(prefers-color-scheme: dark)');

  /** Applies the cached choice and keeps 'system' in step with the OS setting. */
  init(): void {
    this.apply(this.preference);
    this.media.addEventListener('change', () => {
      if (this.preference === 'system') this.apply('system');
    });
  }

  /** The cached choice, or the dark default when none was ever stored. */
  get preference(): ThemePref {
    return this.storedPreference ?? this.defaultTheme;
  }

  /** The cached choice, or null when there is none. */
  get storedPreference(): ThemePref | null {
    let saved: string | null = null;
    try { saved = localStorage.getItem(this.storageKey); } catch { /* ignore */ }
    return isThemePref(saved) ? saved : null;
  }

  /** The account's choice replaces the cache; anything that is not a known value is ignored. */
  applyServerTheme(theme: string | null | undefined): void {
    if (!isThemePref(theme) || theme === this.storedPreference) return;
    try { localStorage.setItem(this.storageKey, theme); } catch { /* storage unavailable */ }
    this.apply(theme);
  }

  private apply(theme: ThemePref): void {
    document.body.classList.toggle('dark', theme === 'dark' || (theme === 'system' && this.media.matches));
  }
}
